/**
 * Modal pro založení nové akce – rozlišuje:
 *   • Hudební akce (gig) – plný záznam (datum, čas, místo, cena, finance…)
 *   • Blokace volna (block) – jen termín pro konkrétní osobu (datum + důvod)
 */
import { supabase } from '../lib/supabase.js';
import { calcLeadTime, calcDepartureTime, getStribroKm } from '../utils/planovac.js';

function esc(s) { if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

async function loadFormData() {
    const [{ data: places }, { data: profiles }, { data: settingsRows }] = await Promise.all([
        supabase.from('places').select('id, name, distance_pilsen, distance_stribro, travel_time_pilsen, travel_time_stribro, extra_reserve_min').order('name'),
        supabase.from('profiles').select('id, name, is_default, share_percent, musician_role, home_city, default_own_transport').order('name'),
        supabase.from('settings').select('key, value')
    ]);
    const settings = Object.fromEntries((settingsRows || []).map(s => [s.key, s.value]));
    return { places: places || [], profiles: profiles || [], settings };
}

/** Posune čas v inputu o delta min. Bezpečně přes půlnoc. */
function shiftTime(input, deltaMin) {
    if (!input.value) {
        input.value = '20:00';
        return;
    }
    const [h, m] = input.value.split(':').map(Number);
    const total = ((h * 60 + m + deltaMin) % 1440 + 1440) % 1440;
    const nh = String(Math.floor(total / 60)).padStart(2, '0');
    const nm = String(total % 60).padStart(2, '0');
    input.value = `${nh}:${nm}`;
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Posune číslo o delta (záporné min 0). */
function shiftNumber(input, delta) {
    const cur = parseFloat(String(input.value).replace(/\s/g, '')) || 0;
    const next = Math.max(0, Math.round(cur + delta));
    input.value = input.dataset.format === 'thousands' ? fmtThousands(next) : String(next);
    input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Naformátuje celé číslo s mezerami jako tisícovým oddělovačem. */
function fmtThousands(n) {
    return new Intl.NumberFormat('cs-CZ').format(n).replace(/ /g, ' ');
}

/** Aktivuje formátování tisíců na text-inputu (focus = bez mezer, blur = s mezerami). */
function setupThousandsInput(input) {
    input.type = 'text';
    input.inputMode = 'numeric';
    input.dataset.format = 'thousands';

    const apply = () => {
        const digits = String(input.value).replace(/[^\d]/g, '');
        if (digits) input.value = fmtThousands(parseInt(digits, 10));
    };
    apply();
    input.addEventListener('focus', () => { input.value = String(input.value).replace(/\s/g, ''); });
    input.addEventListener('blur', apply);
}

/** Vrátí čisté číslo z formátovaného input. */
function readNumeric(v) {
    return parseFloat(String(v ?? '').replace(/\s/g, '')) || 0;
}

/** Spočte sraz + odjezdy z času začátku akce, místa a extra rezerv. */
function computeAutoTimes(timeStart, place, settings, eventExtraMin = 0) {
    if (!timeStart || !place) return {};
    const lead = calcLeadTime(place, settings, eventExtraMin);
    return {
        meeting_time:      calcDepartureTime(timeStart, lead.meetingMin),
        departure_pilsen:  calcDepartureTime(timeStart, lead.pilsenMin),
        departure_stribro: calcDepartureTime(timeStart, lead.stribroMin)
    };
}

function buildModal() {
    let m = document.getElementById('new-event-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'new-event-modal';
    m.className = 'modal-overlay';
    document.body.appendChild(m);
    return m;
}

function gigFormHTML(places, settings, profiles) {
    const placeOpts = places.map(p => `<option value="${p.id}">${esc(p.name)}</option>`).join('');
    const defaultMuzs = (profiles || []).filter(p => p.is_default);
    const timeWithShift = (name, value = '') => `
        <div class="time-with-shift">
            <input type="time" name="${name}" value="${value}" step="60">
            <button type="button" class="shift-btn" data-shift="${name}" data-delta="-30" title="−30 min">−</button>
            <button type="button" class="shift-btn" data-shift="${name}" data-delta="30" title="+30 min">+</button>
        </div>
    `;
    return `
        <div class="spec-section">
            <h4>Základní</h4>
            <div class="spec-row"><label>Datum</label><input type="date" name="event_date" required></div>
            <div class="spec-row"><label>Začátek</label>${timeWithShift('time_start', '20:00')}</div>
            <div class="spec-row"><label>Konec</label>${timeWithShift('time_end', '02:00')}</div>
            <div class="spec-row"><label>Místo konání</label>
                <select name="place_id" style="min-width:220px;">
                    <option value="">– vyber místo –</option>
                    ${placeOpts}
                </select>
            </div>
            <div class="spec-row"><label>Pořadatel</label><input type="text" name="organizer" style="min-width:220px;text-align:left;"></div>
            <div class="spec-row"><label>Telefon pořadatele</label><input type="text" name="organizer_phone" style="min-width:220px;text-align:left;"></div>
            <div class="spec-row"><label>Sraz na místě</label>${timeWithShift('meeting_time')}</div>
            <div class="spec-row"><label>Odjezd z Plzně</label>${timeWithShift('departure_pilsen')}</div>
            <div class="spec-row"><label>Odjezd ze Stříbra</label>${timeWithShift('departure_stribro')}</div>
            <div class="spec-row"><label>Extra rezerva pro akci <span class="unit">ples = víc předem</span></label>
                <div class="extra-reserve-control">
                    <label class="check-inline"><input type="checkbox" name="extra_reserve_active"> aktivní</label>
                    <input type="number" name="extra_reserve_min" value="15" min="0" step="15" disabled>
                    <span class="unit">min</span>
                </div>
            </div>
        </div>
        <div class="spec-section">
            <h4>Finance</h4>
            <div class="spec-row"><label>Cena akce <span class="unit">Kč</span></label>
                <div class="number-with-shift">
                    <input type="number" name="price_total" value="15000" step="500">
                    <button type="button" class="shift-btn" data-shift-num="price_total" data-delta="-500" title="−500 Kč">−</button>
                    <button type="button" class="shift-btn" data-shift-num="price_total" data-delta="500" title="+500 Kč">+</button>
                </div>
            </div>
            <div class="spec-row"><label>Km aparatury <span class="unit">z Plzně</span></label><input type="number" name="equipment_km" value="0"></div>
            <div class="spec-row"><label>Pronájem aparatury <span class="unit">Kč</span></label>
                <div class="number-with-shift">
                    <input type="number" name="equipment_rent" value="${settings.default_equipment_rent ?? 3000}" step="200">
                    <button type="button" class="shift-btn" data-shift-num="equipment_rent" data-delta="-200" title="−200 Kč">−</button>
                    <button type="button" class="shift-btn" data-shift-num="equipment_rent" data-delta="200" title="+200 Kč">+</button>
                </div>
            </div>
            <div class="spec-row"><label>Stravné <span class="unit">Kč</span></label><input type="number" name="food_allowance" value="${settings.default_food_allowance ?? 0}"></div>
            <div class="spec-row"><label>Smluvně</label>
                <select name="contract_signed"><option value="false" selected>Ne</option><option value="true">Ano</option></select>
            </div>
            <div class="spec-row"><label>Forma platby</label>
                <select name="payment_form">
                    <option>Hotově (s dokladem)</option>
                    <option>Faktura</option>
                    <option>Úhrada převodem</option>
                </select>
            </div>
        </div>
        <div class="spec-section">
            <h4>Poznámky</h4>
            <div class="spec-row" style="grid-template-columns: 1fr;">
                <textarea name="notes" rows="2" style="width:100%; padding:6px 10px; border:1px solid var(--border); border-radius:4px; font-family:inherit; font-size:13px;"></textarea>
            </div>
        </div>
        <div class="spec-section">
            <h4>Muzikanti</h4>
            <div id="muzikanti-list" data-default-count="${defaultMuzs.length}">
                ${defaultMuzs.map(p => muzRowHTML(p)).join('') || '<p style="color:var(--text-muted); font-size:12px;">Žádný muzikant nemá default zapnutý. V Nastavení → Databáze muzikantů přepni „Default".</p>'}
            </div>
            <button type="button" id="add-muz-row" class="muz-chip-add" style="margin-top:8px;">+ Přidat dalšího</button>
            <p class="hint" style="margin-top:8px;">
                Fajfka <em>osobní doprava</em> připíše muzikantovi km, jeho cestovné se mu vyplatí (km × 2 × sazba osobního auta).
                Defaultně se km dosadí podle vzdálenosti místa od jeho města (Plzeň / Stříbro).
            </p>
        </div>
    `;
}

function muzRowHTML(profile, km = null, hasTransport = null) {
    const isHost = profile.musician_role === 'stalý host';
    // Pokud volající nevynutil explicitní hodnotu, použij default z profilu muzikanta
    const transport = hasTransport == null ? !!profile.default_own_transport : !!hasTransport;
    return `
        <div class="muz-row" data-profile-id="${profile.id}" data-home="${profile.home_city || 'Plzeň'}" data-share="${profile.share_percent || 100}">
            <label class="check-inline" style="flex:0 0 auto;"><input type="checkbox" class="muz-transport" ${transport ? 'checked' : ''}> osobní doprava</label>
            <span class="muz-row-name">${esc(profile.name)}${isHost ? ` <span class="host-tag">${profile.share_percent}%</span>` : ''} <span class="muz-row-home">(${profile.home_city || 'Plzeň'})</span></span>
            <input type="number" class="muz-row-km" value="${km ?? ''}" placeholder="0" min="0">
            <span class="unit">km</span>
            <button type="button" class="chip-remove muz-row-remove" title="Odebrat">×</button>
        </div>
    `;
}

function blockFormHTML(profiles) {
    const profileOpts = profiles.map(p => `<option value="${p.id}">${esc(p.name)}</option>`).join('');
    return `
        <div class="spec-section">
            <h4>Blokace termínu</h4>
            <p class="hint" style="margin-bottom:10px;">Tenký zápis – pouze datum, osoba a důvod. Termín se zobrazí v Přehledu jako žlutý pruh „Volno".</p>
            <div class="spec-row"><label>Datum od</label><input type="date" name="event_date" required></div>
            <div class="spec-row"><label>Datum do <span class="unit">(volitelné – pro vícedenní)</span></label><input type="date" name="event_date_end"></div>
            <div class="spec-row"><label>Pro koho</label>
                <select name="blocked_for" required style="min-width:220px;">
                    <option value="">– vyber muzikanta –</option>
                    ${profileOpts}
                </select>
            </div>
            <div class="spec-row" style="grid-template-columns: 1fr;">
                <label>Důvod (např. dovolená, lékař)</label>
            </div>
            <div class="spec-row" style="grid-template-columns: 1fr;">
                <input type="text" name="block_reason" placeholder="dovolená, lékař, soukromé volno…" style="width:100%; padding:6px 10px; border:1px solid var(--border); border-radius:4px; font-family:inherit; font-size:13px;">
            </div>
        </div>
    `;
}

function bindMuzTransport(cb, m, places, placeSelect, settings) {
    cb.addEventListener('change', () => {
        const row = cb.closest('.muz-row');
        const kmInput = row.querySelector('.muz-row-km');
        if (!cb.checked) {
            kmInput.value = 0;
        } else {
            const place = places.find(p => p.id === placeSelect?.value);
            const home = row.dataset.home || 'Plzeň';
            kmInput.value = place
                ? (home === 'Plzeň' ? place.distance_pilsen : getStribroKm(place, settings)) || 0
                : 0;
        }
    });
}

function bindMuzRemove(btn) {
    btn.addEventListener('click', () => btn.closest('.muz-row').remove());
}

function openAddMuzMenu(anchor, profiles, m, places, placeSelect, settings) {
    document.querySelectorAll('.muz-add-popup').forEach(el => el.remove());

    const usedIds = new Set(Array.from(m.querySelectorAll('.muz-row')).map(r => r.dataset.profileId));
    const available = profiles.filter(p => !usedIds.has(p.id));
    if (!available.length) { alert('Všichni muzikanti z databáze jsou už přidaní.'); return; }

    const popup = document.createElement('div');
    popup.className = 'muz-add-popup';
    popup.innerHTML = `
        <div class="popup-head">Přidat muzikanta</div>
        <ul>${available.map(p => `<li data-id="${p.id}">${esc(p.name)}${p.musician_role === 'stalý host' ? ` <span class="host-tag" style="margin-left:6px;">${p.share_percent}%</span>` : ''}</li>`).join('')}</ul>
    `;
    const rect = anchor.getBoundingClientRect();
    popup.style.top = (rect.bottom + window.scrollY + 4) + 'px';
    popup.style.left = (rect.left + window.scrollX) + 'px';
    document.body.appendChild(popup);
    const close = () => popup.remove();
    setTimeout(() => {
        document.addEventListener('click', function once(e) {
            if (!popup.contains(e.target)) { close(); document.removeEventListener('click', once); }
        });
    }, 0);
    popup.querySelectorAll('li').forEach(li => {
        li.addEventListener('click', () => {
            const profile = profiles.find(p => p.id === li.dataset.id);
            const list = m.querySelector('#muzikanti-list');
            // Pro dodatečné muzikanty respektuj jejich default_own_transport — když true,
            // zaškrtni checkbox i předvyplní km podle aktuálně vybraného místa
            const hasTransport = !!profile.default_own_transport;
            let initialKm = 0;
            if (hasTransport) {
                const place = places.find(p => p.id === placeSelect?.value);
                const home = profile.home_city || 'Plzeň';
                initialKm = place
                    ? (home === 'Plzeň' ? place.distance_pilsen : getStribroKm(place, settings)) || 0
                    : 0;
            }
            const html = muzRowHTML(profile, initialKm, hasTransport);
            list.insertAdjacentHTML('beforeend', html);
            const newRow = list.lastElementChild;
            bindMuzTransport(newRow.querySelector('.muz-transport'), m, places, placeSelect, settings);
            bindMuzRemove(newRow.querySelector('.muz-row-remove'));
            close();
        });
    });
}

async function submitGig(form, settings) {
    const fd = new FormData(form);
    const placeId = fd.get('place_id');
    const insert = {
        type: 'gig',
        event_date: fd.get('event_date'),
        time_start: fd.get('time_start') || null,
        time_end: fd.get('time_end') || null,
        place_id: placeId || null,
        organizer: fd.get('organizer') || null,
        organizer_phone: fd.get('organizer_phone') || null,
        meeting_time: fd.get('meeting_time') || null,
        departure_pilsen: fd.get('departure_pilsen') || null,
        departure_stribro: fd.get('departure_stribro') || null,
        extra_reserve_min: fd.get('extra_reserve_active') === 'on'
            ? (parseInt(fd.get('extra_reserve_min'), 10) || 15)
            : 0,
        price_total: readNumeric(fd.get('price_total')),
        equipment_km: parseInt(fd.get('equipment_km'), 10) || 0,
        equipment_rent: readNumeric(fd.get('equipment_rent')),
        food_allowance: readNumeric(fd.get('food_allowance')),
        contract_signed: fd.get('contract_signed') === 'true',
        payment_form: fd.get('payment_form') || 'Hotově (s dokladem)',
        notes: fd.get('notes') || null,
        // snapshot sazeb
        rate_van_per_km: Number(settings.rate_van_per_km) || 12,
        rate_car_per_km: Number(settings.rate_car_per_km) || 8,
        target_band_wage: Number(settings.target_band_wage) || 500,
        rounding_step: Number(settings.rounding_step) || 100
    };

    const { data: ev, error } = await supabase.from('events').insert(insert).select().single();
    if (error) return { error };

    // Načti muzikanty přímo z formuláře (řádky s checkboxem + km)
    const rows = [];
    form.querySelectorAll('.muz-row').forEach(row => {
        const id = row.dataset.profileId;
        if (!id) return;
        const cb = row.querySelector('.muz-transport');
        const kmRaw = row.querySelector('.muz-row-km')?.value;
        const km = cb?.checked ? (parseInt(kmRaw, 10) || 0) : 0;
        const share = parseInt(row.dataset.share, 10) || 100;
        rows.push({ event_id: ev.id, musician_id: id, km, share_percent: share });
    });
    if (rows.length) await supabase.from('event_musicians').insert(rows);

    return { event: ev };
}

async function submitBlock(form) {
    const fd = new FormData(form);
    const insert = {
        type: 'block',
        event_date: fd.get('event_date'),
        event_date_end: fd.get('event_date_end') || null,
        blocked_for: fd.get('blocked_for'),
        block_reason: fd.get('block_reason') || null
    };
    const { data, error } = await supabase.from('events').insert(insert).select().single();
    return error ? { error } : { event: data };
}

export async function openNewEventModal(onSuccess) {
    const m = buildModal();
    const { places, profiles, settings } = await loadFormData();

    let currentType = 'gig';

    function render() {
        m.innerHTML = `
            <div class="modal-window" style="max-width: 640px;">
                <div class="modal-head">
                    <h2>Nová akce</h2>
                    <button class="modal-close" type="button" id="ne-close">×</button>
                </div>
                <div class="modal-body">
                    <div style="display:flex; gap:6px; margin-bottom:14px; background:var(--surface-alt); padding:4px; border-radius:8px;">
                        <button type="button" class="type-btn ${currentType === 'gig' ? 'active' : ''}" data-type="gig"
                            style="flex:1; padding:8px 14px; border:none; background:${currentType === 'gig' ? 'var(--surface)' : 'transparent'}; border-radius:6px; cursor:pointer; font-family:inherit; font-weight:600; font-size:14px; color:${currentType === 'gig' ? 'var(--accent)' : 'var(--text-muted)'}; box-shadow:${currentType === 'gig' ? 'var(--shadow)' : 'none'};">
                            Hudební akce
                        </button>
                        <button type="button" class="type-btn ${currentType === 'block' ? 'active' : ''}" data-type="block"
                            style="flex:1; padding:8px 14px; border:none; background:${currentType === 'block' ? 'var(--surface)' : 'transparent'}; border-radius:6px; cursor:pointer; font-family:inherit; font-weight:600; font-size:14px; color:${currentType === 'block' ? 'var(--warning)' : 'var(--text-muted)'}; box-shadow:${currentType === 'block' ? 'var(--shadow)' : 'none'};">
                            Blokace volna
                        </button>
                    </div>
                    <form id="ne-form">
                        ${currentType === 'gig' ? gigFormHTML(places, settings, profiles) : blockFormHTML(profiles)}
                        <p id="ne-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
                    </form>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="ne-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="button" id="ne-submit">${currentType === 'gig' ? 'Vytvořit akci' : 'Vytvořit blokaci'}</button>
                </div>
            </div>
        `;

        m.querySelector('#ne-close').onclick  = close;
        m.querySelector('#ne-cancel').onclick = close;
        m.onclick = (e) => { if (e.target === m) close(); };

        m.querySelectorAll('.type-btn').forEach(btn => {
            btn.onclick = () => { currentType = btn.dataset.type; render(); };
        });

        // Formátování tisíců na finančních polích
        ['price_total', 'equipment_rent', 'food_allowance'].forEach(name => {
            const el = m.querySelector(`[name="${name}"]`);
            if (el) setupThousandsInput(el);
        });

        // Šipky ±30 min vedle time inputů + ±X Kč vedle číselných
        m.querySelectorAll('.shift-btn').forEach(btn => {
            btn.onclick = () => {
                const timeName = btn.dataset.shift;
                const numName  = btn.dataset.shiftNum;
                const delta = parseInt(btn.dataset.delta, 10);
                if (timeName) {
                    const input = m.querySelector(`[name="${timeName}"]`);
                    if (input) shiftTime(input, delta);
                } else if (numName) {
                    const input = m.querySelector(`[name="${numName}"]`);
                    if (input) shiftNumber(input, delta);
                }
            };
        });

        // Auto-vyplnění Sraz/odjezdů po výběru místa nebo změně začátku
        const placeSelect = m.querySelector('[name="place_id"]');
        const startInput  = m.querySelector('[name="time_start"]');
        const extraInput  = m.querySelector('[name="extra_reserve_min"]');
        const extraActive = m.querySelector('[name="extra_reserve_active"]');
        if (extraActive && extraInput) {
            extraActive.addEventListener('change', () => {
                extraInput.disabled = !extraActive.checked;
                if (extraActive.checked && (parseInt(extraInput.value, 10) || 0) === 0) extraInput.value = 15;
                autofill();
            });
        }
        function autofill() {
            const placeId = placeSelect?.value;
            const place = places.find(p => p.id === placeId);
            const start = startInput?.value;
            const extra = (extraActive?.checked ? (parseInt(extraInput?.value, 10) || 0) : 0);
            if (!place) return;

            // Km aparatury = vzdálenost od Plzně (aparatura jezdí vždy z Plzně)
            const kmInput = m.querySelector('[name="equipment_km"]');
            if (kmInput && place.distance_pilsen != null) kmInput.value = place.distance_pilsen;

            if (!start) return;
            const t = computeAutoTimes(start, place, settings, extra);
            const apply = (name, val) => {
                const el = m.querySelector(`[name="${name}"]`);
                if (el && val) el.value = val;
            };
            apply('meeting_time', t.meeting_time);
            apply('departure_pilsen', t.departure_pilsen);
            apply('departure_stribro', t.departure_stribro);
        }
        if (placeSelect) placeSelect.addEventListener('change', autofill);
        if (startInput)  startInput.addEventListener('input', autofill);
        if (extraInput)  extraInput.addEventListener('input', autofill);

        // Auto-doplnění km muzikantů podle home_city + vybraného místa
        function autofillMuzKm() {
            const place = places.find(p => p.id === placeSelect?.value);
            if (!place) return;
            m.querySelectorAll('.muz-row').forEach(row => {
                const home = row.dataset.home || 'Plzeň';
                const km = home === 'Plzeň'
                    ? (place.distance_pilsen || 0)
                    : getStribroKm(place, settings);
                const kmInput = row.querySelector('.muz-row-km');
                const cb = row.querySelector('.muz-transport');
                if (cb.checked && !kmInput.value) kmInput.value = km;
            });
        }
        if (placeSelect) placeSelect.addEventListener('change', autofillMuzKm);

        // Checkbox osobní doprava: zaškrtnuto = doplň km, odškrtnuto = vynuluj
        m.querySelectorAll('.muz-transport').forEach(cb => bindMuzTransport(cb, m, places, placeSelect, settings));
        m.querySelectorAll('.muz-row-remove').forEach(btn => bindMuzRemove(btn));

        // + Přidat dalšího muzikanta
        const addBtn = m.querySelector('#add-muz-row');
        if (addBtn) addBtn.addEventListener('click', () => openAddMuzMenu(addBtn, profiles, m, places, placeSelect, settings));

        m.querySelector('#ne-submit').onclick = async () => {
            const form = m.querySelector('#ne-form');
            if (!form.checkValidity()) { form.reportValidity(); return; }
            const errEl = m.querySelector('#ne-error');
            errEl.style.display = 'none';
            const submitBtn = m.querySelector('#ne-submit');
            submitBtn.disabled = true; submitBtn.textContent = 'Ukládám…';

            const r = currentType === 'gig' ? await submitGig(form, settings) : await submitBlock(form);
            if (r.error) {
                errEl.textContent = 'Chyba: ' + r.error.message;
                errEl.style.display = 'block';
                submitBtn.disabled = false;
                submitBtn.textContent = currentType === 'gig' ? 'Vytvořit akci' : 'Vytvořit blokaci';
                return;
            }
            close();
            onSuccess?.();
        };
    }

    function close() { m.remove(); }
    render();
    m.classList.add('open');
}
